import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
} from "@mui/material";
import axios from "../../utils/axiosInstance";

import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { sortAlphabetically } from "../../utils/sortAlphabetically";
import { getUIDescription } from "../SiteAdmin/customerGroupDetails/AssigningCustomerGroupDevice";

export default function AddNotificationGroup({ open, onClose, onSubmit }) {
  const [groupName, setGroupName] = useState("");
  const [description, setDescription] = useState("");
  const [parentGroup, setParentGroup] = useState("");
  const [region, setRegion] = useState("");

  // 🔹 State for groups
  const [allGroups, setAllGroups] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(false);

  // 🔹 State for regions
  const [allRegions, setAllRegions] = useState([]);
  const [loadingRegions, setLoadingRegions] = useState(false);

  // 🔹 Fetch groups on mount
  useEffect(() => {
    const fetchGroups = async () => {
      try {
        setLoadingGroups(true);
        const response = await axios.get(
          `${process.env.REACT_APP_API_GATEAWAY}/groups?type=geographical`,
          {
            params: { companyId: process.env.REACT_APP_COMPANY_ID },
          }
        );
        const excludedIds = [
          "GROUP-2020-06-06-03-15-14-925896",
          "GROUP-2020-11-26-13-46-30-962731",
        ]; // 👈 IDs to exclude

        const filtered = await response.data.filter(
          (group) => !excludedIds.includes(group.id)
        );
        const sortedData = sortAlphabetically(filtered, "name");
        setAllGroups(sortedData);
      } catch (err) {
        console.error("❌ Error fetching groups:", err);
      } finally {
        setLoadingGroups(false);
      }
    };

    if (open) {
      // Only fetch when dialog opens
      fetchGroups();
    }
  }, [open]);

  // 🔹 Fetch regions when parent group changes
  useEffect(() => {
    if (!parentGroup) {
      setAllRegions([]);
      setRegion(""); // Reset region when parent group changes
      return;
    }

    const fetchRegions = async () => {
      try {
        setLoadingRegions(true);
        const response = await axios.get(
          `${process.env.REACT_APP_API_GATEAWAY}/regions?groupId=${parentGroup}`,
          {
            params: { companyId: process.env.REACT_APP_COMPANY_ID },
          }
        );
        setAllRegions(response.data);
      } catch (err) {
        console.error("❌ Error fetching regions:", err);
        setAllRegions([]);
      } finally {
        setLoadingRegions(false);
      }
    };

    fetchRegions();
  }, [parentGroup]);

  const handleAddGroupNotif = async () => {
    try {
      const payload = {
        companyId: process.env.REACT_APP_COMPANY_ID,
        name: groupName,
        description: description,
        groupId: parentGroup,
        regionId: region || undefined, // 🔧 Fixed: Use region state instead of empty string
        UI_Description: `${getUIDescription()}\n add notification group`,
      };

      const response = await axios.post(
        `${process.env.REACT_APP_API_GATEAWAY}/notificationGroups/`,
        payload
      );

      // 🔧 Fixed: Create properly structured object for the parent component
      const newNotificationGroup = {
        id: response.data.id,
        name: response.data.name,
        description: response.data.description,
        date_created: response.data.date_created || new Date().toISOString(),
        users: response.data.users || 0,
        devices: response.data.devices || 0,
        // 🔧 Include nested objects that the table expects
        group: allGroups.find((g) => g.id === parentGroup) || {
          name: "Unknown Group",
        },
        region: allRegions.find((r) => r.id === region) || null,
      };

      if (onSubmit) {
        onSubmit(newNotificationGroup); // 🔧 Pass the structured object
      }
      toast.success("✅ Notification group created successfully!");
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
          "❌ Failed to create notification group"
      );
    } finally {
      onClose();
      // Reset form
      setGroupName("");
      setDescription("");
      setParentGroup("");
      setRegion("");
      setAllRegions([]);
    }
  };

  const handleClose = () => {
    onClose();
    // Reset form when closing
    setGroupName("");
    setDescription("");
    setParentGroup("");
    setRegion("");
    setAllRegions([]);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ mt: 1, fontSize: "0.93rem" }}>
        Notification Group: users in this group get alerts from all units in the
        Parent group
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
          <TextField
            label="User Group Name"
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
            fullWidth
            required
          />
          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
          />
          {/* 🔹 Parent Group Dropdown */}
          <TextField
            select
            label="Parent Group"
            value={parentGroup}
            onChange={(e) => setParentGroup(e.target.value)}
            fullWidth
            disabled={loadingGroups}
            required
          >
            {allGroups.length > 0 ? (
              allGroups.map((group) => (
                <MenuItem key={group.id} value={group.id}>
                  {group.name}
                </MenuItem>
              ))
            ) : (
              <MenuItem disabled>
                {loadingGroups ? "Loading groups..." : "No groups available"}
              </MenuItem>
            )}
          </TextField>

          {/* Region */}
          <TextField
            select
            label="Region (Optional)"
            value={region}
            onChange={(e) => setRegion(e.target.value)}
            fullWidth
            disabled={loadingRegions || !parentGroup}
          >
            <MenuItem value="">
              <em>No Region Selected</em>
            </MenuItem>
            {allRegions.length > 0 ? (
              allRegions.map((regionItem) => (
                <MenuItem key={regionItem.id} value={regionItem.id}>
                  {regionItem.name}
                </MenuItem>
              ))
            ) : (
              <MenuItem disabled>
                {loadingRegions
                  ? "Loading Regions..."
                  : parentGroup
                  ? "No Regions available"
                  : "Select Parent Group first"}
              </MenuItem>
            )}
          </TextField>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          sx={{
            borderRadius: 0, // ✅ remove rounding
          }}
          onClick={handleClose}
          color="error"
        >
          Cancel
        </Button>
        <Button
          onClick={handleAddGroupNotif}
          variant="contained"
          color="primary"
          disabled={!groupName.trim() || !parentGroup}
        >
          Add Notification Group
        </Button>
      </DialogActions>
    </Dialog>
  );
}
